import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Building2, Search, Filter, Loader, AlertCircle, CheckCircle,
  X, Eye, Edit2, Key, Trash2, ChevronLeft, ChevronRight,
  Activity, Shield, Mail, CreditCard, Calendar, MessageSquare,
  FileText, ArrowUpRight, Zap, Award, ExternalLink,
  Crown, User, Tag, Save, Info, Trash, RefreshCw, ShieldCheck,
  SlidersHorizontal, ChevronDown, Ban, PlusCircle
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';

// Cross-tenant aggregate view — platform billing is always reported in the
// platform's own currency, not any individual tenant's setting.
const PLATFORM_CURRENCY = 'INR';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
];

function StatusBadge({ active }) {
  return active
    ? <Badge variant="emerald"><div className="w-1.5 h-1.5 rounded-full bg-[#2E844A] mr-2 " /> Account Active</Badge>
    : <Badge variant="rose"><div className="w-1.5 h-1.5 rounded-full bg-[#BA0517] mr-2 " /> Inactive</Badge>;
}

function EditModal({ company, plans, onClose, onSaved }) {
  const [form, setForm] = useState({
    isActive: company.isActive !== false,
    isSubscriptionActive: company.isSubscriptionActive !== false,
    planId: company.subscriptionPlan?._id || company.subscriptionPlan || '',
    daysToAdd: '',
    role: company.role || 'user',
    name: company.companyName || company.name || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { ...form };
      if (!payload.daysToAdd) delete payload.daysToAdd;
      await api.put(`/admin/companies/${company._id}`, payload);
      alertService.toast('success', 'Business profile updated successfully');
      onSaved();
    } catch (e) {
      alertService.error('Update Failed', e.response?.data?.error || 'Failed to update company record.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-2xl p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#E5E5E5] bg-white rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
           <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3]">
                 <Edit2 size={20} strokeWidth={2} />
              </div>
              <div className="space-y-1">
                 <h2 className="text-[16px] font-bold text-[#2E2E2E]">Manage Client Details</h2>
                 <p className="text-[13px] text-[#706E6B]">{company.companyName || company.name}</p>
              </div>
           </div>
           <button className="text-[#706E6B] hover:text-[#080707] hover:bg-[#F3F2F2] p-2 rounded-md transition-colors" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Company Name</label>
               <Input 
                 icon={Building2}
                 value={form.name} 
                 onChange={e => set('name', e.target.value)} 
               />
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Access Role</label>
               <Select 
                 icon={User}
                 value={form.role} 
                 onChange={e => set('role', e.target.value)}
               >
                 <option value="user">Platform User</option>
                 <option value="admin">System Administrator</option>
               </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Assigned Plan</label>
               <Select 
                 icon={CreditCard}
                 value={form.planId} 
                 onChange={e => set('planId', e.target.value)}
               >
                 <option value="">Select Plan Tier…</option>
                 {plans.map(p => (
                   <option key={p._id} value={p._id}>{p.name} ({formatCurrency(p.price, PLATFORM_CURRENCY)})</option>
                 ))}
               </Select>
            </div>
            <div className="space-y-2">
               <label className="text-[12px] font-semibold text-[#444444]">Extension Period</label>
               <Input 
                 icon={Calendar}
                 type="number" 
                 placeholder="Days to add" 
                 value={form.daysToAdd} 
                 onChange={e => set('daysToAdd', e.target.value)} 
                 hint={company.subscriptionEndDate ? `Valid until: ${new Date(company.subscriptionEndDate).toLocaleDateString()}` : 'No expiry set'}
               />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <label className="flex items-center justify-between p-4 rounded-lg bg-white border border-[#E5E5E5] hover:bg-[#F4F6F9] transition-all cursor-pointer group shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                <div className="space-y-1">
                   <span className="block text-[13px] font-bold text-[#080707] leading-none">Login Access</span>
                   <span className="block text-[12px] text-[#706E6B]">Enable account session login</span>
                </div>
                <div className={cn(
 "w-10 h-5 rounded-full relative transition-all",
 form.isActive ? "bg-[#0176D3]" : "bg-[#D8DDE6]"
 )}>
                  <div className={cn(
 "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm",
 form.isActive ? "left-[22px]" : "left-0.5"
 )} />
                </div>
                <input type="checkbox" className="hidden" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
             </label>

             <label className="flex items-center justify-between p-4 rounded-lg bg-white border border-[#E5E5E5] hover:bg-[#F4F6F9] transition-all cursor-pointer group shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                <div className="space-y-1">
                   <span className="block text-[13px] font-bold text-[#080707] leading-none">Subscription Override</span>
                   <span className="block text-[12px] text-[#706E6B]">Bypass billing restrictions</span>
                </div>
                <div className={cn(
 "w-10 h-5 rounded-full relative transition-all",
 form.isSubscriptionActive ? "bg-[#0176D3]" : "bg-[#D8DDE6]"
 )}>
                  <div className={cn(
 "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm",
 form.isSubscriptionActive ? "left-[22px]" : "left-0.5"
 )} />
                </div>
                <input type="checkbox" className="hidden" checked={form.isSubscriptionActive} onChange={e => set('isSubscriptionActive', e.target.checked)} />
             </label>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2 bg-white">
          <button className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2] transition-colors" onClick={onClose}>Cancel</button>
          <button 
            className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            onClick={handleSave} 
            disabled={saving}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            Save Profile
          </button>
        </div>
      </Card>
    </div>
  );
}

// Phase 5 — per-company exceptions on top of whatever plan this company is
// assigned. A feature toggle is a 3-way cycle (plan default → forced on →
// forced off → back to default) rather than a checkbox, since "no override"
// is a real, distinct state from both "on" and "off". Limit fields are
// plain numbers with a "Reset" back to null (= use the plan's own value).
function OverridesModal({ company, catalogGroups, onClose, onSaved }) {
  const [featureOverrides, setFeatureOverrides] = useState({
    grant: company.featureOverrides?.grant || [],
    revoke: company.featureOverrides?.revoke || [],
  });
  const [limitOverrides, setLimitOverrides] = useState({
    maxDevices: company.limitOverrides?.maxDevices ?? '',
    maxUsers: company.limitOverrides?.maxUsers ?? '',
    monthlyMessageLimit: company.limitOverrides?.monthlyMessageLimit ?? '',
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [saving, setSaving] = useState(false);

  const stateOf = (key) => (featureOverrides.grant.includes(key) ? 'grant' : featureOverrides.revoke.includes(key) ? 'revoke' : 'default');

  const cycleFeature = (key) => {
    const current = stateOf(key);
    setFeatureOverrides((f) => {
      const grant = f.grant.filter((k) => k !== key);
      const revoke = f.revoke.filter((k) => k !== key);
      if (current === 'default') return { grant: [...grant, key], revoke };
      if (current === 'grant') return { grant, revoke: [...revoke, key] };
      return { grant, revoke }; // 'revoke' -> back to default
    });
  };

  const setLimit = (name, val) => setLimitOverrides((l) => ({ ...l, [name]: val }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const toNum = (v) => (v === '' ? null : Number(v));
      await api.put(`/admin/companies/${company._id}/overrides`, {
        featureOverrides,
        limitOverrides: {
          maxDevices: toNum(limitOverrides.maxDevices),
          maxUsers: toNum(limitOverrides.maxUsers),
          monthlyMessageLimit: toNum(limitOverrides.monthlyMessageLimit),
        },
      });
      alertService.toast('success', 'Custom overrides saved');
      onSaved();
    } catch (e) {
      alertService.error('Save Failed', e.response?.data?.error || 'Failed to save overrides.');
    } finally {
      setSaving(false);
    }
  };

  const grantCount = featureOverrides.grant.length;
  const revokeCount = featureOverrides.revoke.length;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-2xl p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#E5E5E5] bg-white rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3]">
              <SlidersHorizontal size={20} strokeWidth={2} />
            </div>
            <div className="space-y-1">
              <h2 className="text-[16px] font-bold text-[#2E2E2E]">Custom Overrides</h2>
              <p className="text-[13px] text-[#706E6B]">{company.companyName || company.name} — on top of its plan ({company.subscriptionPlan?.name || 'no plan'})</p>
            </div>
          </div>
          <button className="text-[#706E6B] hover:text-[#080707] hover:bg-[#F3F2F2] p-2 rounded-md transition-colors" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          <div className="p-3 rounded-md bg-[#EAF4FE] border border-[#0176D3]/20 text-[12px] text-[#0176D3] font-medium">
            Click a feature to cycle: plan default → force on ({grantCount} granted) → force off ({revokeCount} revoked) → back to default. A limit left blank uses the plan's own value.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Max Devices</label>
              <Input type="number" min="1" placeholder="(plan default)" value={limitOverrides.maxDevices} onChange={e => setLimit('maxDevices', e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Max Seats</label>
              <Input type="number" min="1" placeholder="(plan default)" value={limitOverrides.maxUsers} onChange={e => setLimit('maxUsers', e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Monthly Message Limit</label>
              <Input type="number" min="1" placeholder="(plan default)" value={limitOverrides.monthlyMessageLimit} onChange={e => setLimit('monthlyMessageLimit', e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold text-[#444444]">Feature Overrides</label>
            {Object.entries(catalogGroups).map(([groupName, items]) => {
              const isCollapsed = collapsedGroups[groupName];
              const touched = items.filter(f => stateOf(f.key) !== 'default').length;
              return (
                <div key={groupName} className="border border-[#D8DDE6] rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCollapsedGroups(c => ({ ...c, [groupName]: !c[groupName] }))}
                    className="w-full flex items-center gap-2 p-3 bg-[#F4F6F9] text-[12px] font-bold text-[#080707]"
                  >
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    {groupName}
                    {touched > 0 && <span className="text-[11px] font-normal text-[#0176D3]">({touched} overridden)</span>}
                  </button>
                  {!isCollapsed && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                      {items.map((feat) => {
                        const state = stateOf(feat.key);
                        return (
                          <button
                            key={feat.key}
                            type="button"
                            onClick={() => cycleFeature(feat.key)}
                            className={cn(
 "flex items-center gap-2 p-2 rounded-md border transition-all text-left",
 state === 'grant' && "bg-[#EBF7E6] border-[#2E844A] text-[#2E844A]",
 state === 'revoke' && "bg-[#FEF1EE] border-[#BA0517] text-[#BA0517]",
 state === 'default' && "bg-white border-[#D8DDE6] text-[#080707] hover:bg-[#F4F6F9]"
 )}
                          >
                            {state === 'grant' && <PlusCircle size={14} className="shrink-0" />}
                            {state === 'revoke' && <Ban size={14} className="shrink-0" />}
                            {state === 'default' && <div className="w-3.5 h-3.5 shrink-0" />}
                            <span className="text-[12px] font-semibold truncate flex-1">{feat.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2 bg-white">
          <button className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2] transition-colors" onClick={onClose}>Cancel</button>
          <button
            className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            Save Overrides
          </button>
        </div>
      </Card>
    </div>
  );
}

function DetailModal({ company, onClose, onEdit }) {
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/companies/${company._id}/invoices`)
      .then(res => { if (!cancelled) setRecentInvoices(res.data.invoices || []); })
      .catch(() => { if (!cancelled) setRecentInvoices([]); });
    return () => { cancelled = true; };
  }, [company._id]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-4xl p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#E5E5E5] bg-white rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-8 border-b border-[#D8DDE6] flex items-center justify-between bg-white">
           <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3]">
                 <Building2 size={32} strokeWidth={2} />
              </div>
              <div className="space-y-2">
                 <h2 className="text-2xl font-bold text-[#080707] tracking-tight">{company.companyName || company.name}</h2>
                 <div className="flex items-center gap-3">
                    <StatusBadge active={company.isActive !== false} />
                    <Badge variant={company.role === 'admin' ? 'blue' : 'outline'} className="font-semibold text-[11px]">{company.role === 'admin' ? 'Admin' : 'User'}</Badge>
                    <Badge variant="blue" className="bg-[#0176D3] text-white font-semibold text-[11px]">{company.subscriptionPlan?.name || 'Free'} Plan</Badge>
                 </div>
              </div>
           </div>
           <div className="flex gap-3">
              <button onClick={onEdit} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap text-[13px] font-semibold hover:bg-[#F3F2F2] transition-colors flex items-center gap-2">
                 <Edit2 size={14} /> Edit Profile
              </button>
              <button className="w-9 h-9 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#706E6B] hover:text-[#080707] hover:bg-[#F3F2F2] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]" onClick={onClose}><X size={18} /></button>
           </div>
        </div>

        <div className="p-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 bg-[#F4F6F9]">
           {[
             { icon: Mail, label: 'Contact Email', val: company.email, color: 'text-[#0176D3]' },
             { icon: Calendar, label: 'Expiry Date', val: company.subscriptionEndDate ? new Date(company.subscriptionEndDate).toLocaleDateString() : 'Permanent', color: 'text-[#DD7A01]' },
           ].map((d, i) => (
              <div key={i} className="p-5 rounded-lg bg-white border border-[#E5E5E5] space-y-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                 <div className="flex items-center gap-2 text-[#706E6B]">
                    <d.icon size={16} className={d.color} />
                    <span className="text-[12px] font-semibold">{d.label}</span>
                 </div>
                 <div className="text-[18px] font-bold text-[#080707] truncate">{d.val}</div>
              </div>
           ))}
        </div>

        {recentInvoices.length > 0 && (
          <div className="p-8 border-t border-[#D8DDE6] bg-white">
             <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-md bg-[#F4F6F9] flex items-center justify-center text-[#0176D3]">
                   <FileText size={16} />
                </div>
                <h3 className="text-[15px] font-bold text-[#080707]">Payment History</h3>
             </div>
             <div className="overflow-hidden rounded-md border border-[#D8DDE6]">
                <div className="slds2-table-wrap overflow-x-auto">
                <table className="w-full text-left border-collapse">
                   <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                      <tr className="text-[12px] font-semibold text-[#706E6B]">
                         <th className="p-4 border-b border-[#D8DDE6] font-semibold">Settlement Date</th>
                         <th className="p-4 border-b border-[#D8DDE6] font-semibold">Credit Amount</th>
                         <th className="p-4 border-b border-[#D8DDE6] text-right font-semibold">Invoice Status</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-[#D8DDE6]">
                      {recentInvoices.map(inv => (
                         <tr key={inv._id} className="hover:bg-[#F7F9FB] transition-colors">
                            <td className="p-4 text-[13px] text-[#080707]">{new Date(inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                            <td className="p-4 text-[14px] font-bold text-[#080707] tabular-nums">{formatCurrency(inv.amount?.totalAmount ?? inv.amount ?? 0, PLATFORM_CURRENCY)}</td>
                            <td className="p-4 text-right">
                               <Badge variant={inv.status === 'paid' ? 'emerald' : 'rose'} className="text-[11px] font-semibold">
                                  {inv.status}
                               </Badge>
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
                </div>
             </div>
          </div>
        )}

        <div className="p-8 bg-[#ffffff]/40 border-t border-[#D8DDE6] flex items-center justify-between text-[9px] font-bold text-[#080707] uppercase ">
           <div className="flex items-center gap-3">
              <Activity size={14} className="text-[#0176D3]" /> Administrative Portal
           </div>
           <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#2E844A] " /> Data Integrity: Secure</div>
           </div>
        </div>
      </Card>
    </div>
  );
}

export default function SuperAdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null); // detail modal
  const [editing, setEditing] = useState(null);   // edit modal
  const [overriding, setOverriding] = useState(null); // custom-overrides modal
  const [catalogGroups, setCatalogGroups] = useState({});
  const [keyGenId, setKeyGenId] = useState(null);
  const [keyGenLoading, setKeyGenLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const searchTimer = useRef(null);
  const LIMIT = 15;

  const loadPlans = useCallback(async () => {
    try {
      const res = await api.get('/admin/plans');
      setPlans(res.data.plans || []);
    } catch { }
  }, []);

  // Feeds OverridesModal's grouped feature picker — same catalog SuperAdminPlans uses.
  useEffect(() => {
    api.get('/admin/feature-catalog')
      .then(res => setCatalogGroups(res.data.groups || {}))
      .catch(() => {});
  }, []);

  const load = useCallback(async (p = 1, q = search, s = statusFilter) => {
    try {
      setLoading(true);
      setError('');
      const params = { page: p, limit: LIMIT };
      if (q) params.search = q;
      if (s) params.status = s;
      const res = await api.get('/admin/companies', { params });
      setCompanies(res.data.companies || []);
      setTotal(res.data.pagination?.total || 0);
      setPage(p);
    } catch (e) {
      alertService.error('Connection Error', e.response?.data?.error || 'Failed to load business records.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { load(1, search, statusFilter); }, [statusFilter]); // eslint-disable-line

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, val, statusFilter), 400);
  };

  const handleDelete = async (id, name) => {
    const isConfirmed = await alertService.confirm(
      'Delete Business Profile?',
      `Are you sure you want to permanently delete "${name}"? This action cannot be reversed.`,
      'danger',
      'Delete Record'
    );
    
    if (!isConfirmed) return;

    try {
      await api.delete(`/admin/companies/${id}`);
      alertService.toast('success', 'Business profile purged from system');
      load(page);
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to delete record.');
    }
  };

  const handleGenKey = async (companyId) => {
    try {
      setKeyGenLoading(true);
      setKeyGenId(companyId);
      const res = await api.post(`/admin/companies/${companyId}/api-key`);
      setGeneratedKey(res.data.apiKey);
    } catch (e) {
      alertService.error('Generation Failed', e.response?.data?.error || 'Failed to generate security key.');
      setKeyGenId(null);
    } finally {
      setKeyGenLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="Company Directory" icon={Building2} subtitle={`Monitor and manage all business accounts. Current accounts: ${total}`} />

      {/* Generated Key Security Alert */}
      {generatedKey && (
        <Card className="mb-6 p-6 rounded-lg bg-[#F4F6F9] border border-[#E5E5E5] flex flex-col gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)] animate-in slide-in-from-top-2">
           <div className="space-y-2">
              <h3 className="text-[16px] font-bold text-[#080707] flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#0176D3]" />
                Security Key Generated
              </h3>
               <p className="text-[13px] text-[#706E6B]">The secret key has been successfully established. Copy the signature below immediately. For security, this record will not be visible after this session.</p>
           </div>
           <div className="flex flex-col sm:flex-row items-center gap-4">
              <code className="flex-1 p-3 rounded-lg bg-white border border-[#E5E5E5] text-[#080707] font-mono text-[13px] break-all">
                 {generatedKey}
              </code>
              <button className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] transition-colors whitespace-nowrap" onClick={() => { setGeneratedKey(''); setKeyGenId(null); }}>Dismiss Alert</button>
           </div>
        </Card>
      )}

      {/* Filter Options */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
         <div className="flex-1 max-w-sm">
            <Input
              icon={Search}
              className="h-9"
              placeholder="Search by name, email or identifier…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
         </div>
         <div className="flex gap-3">
            <Select
              icon={Filter}
              className="min-w-[200px] h-9"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <button className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap text-[13px] font-semibold hover:bg-[#F3F2F2] transition-colors flex items-center gap-2" onClick={() => load(1, search, statusFilter)}>
              <RefreshCw size={14} /> Refresh Records
            </button>
         </div>
      </div>

      <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="px-6 py-4 border-b border-[#D8DDE6] flex items-center justify-between">
           <div className="flex items-center gap-2 text-[#080707] font-bold">
              <Activity size={16} className="text-[#0176D3]" /> Account Registry
           </div>
           <div className="hidden sm:flex items-center gap-6 text-[12px] text-[#706E6B]">
              <span>Displaying: <span className="font-semibold text-[#080707]">{((page-1)*LIMIT)+1} - {Math.min(page*LIMIT, total)} / {total}</span></span>
           </div>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          {loading ? (
            <div className="py-40 flex flex-col items-center justify-center gap-8 opacity-30">
               <Loader size={48} className="animate-spin text-[#0176D3]" />
               <p className="text-[11px] font-bold uppercase text-[#706E6B] animate-pulse">Syncing Registry Records…</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="py-40 flex flex-col items-center justify-center text-center space-y-8 opacity-40">
               <div className="w-24 h-24 rounded bg-[#ffffff] flex items-center justify-center text-[#080707] shadow-inner">
                  <Shield size={56} strokeWidth={1} />
               </div>
               <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-[#080707] tracking-tight">No Accounts Found</h3>
                  <p className="text-[10px] font-bold text-[#080707] uppercase max-w-sm mx-auto leading-relaxed">No records detected within current query parameters.</p>
               </div>
            </div>
          ) : (
            <div className="slds2-table-wrap overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F4F6F9] text-[12px] font-semibold text-[#706E6B]">
                  <th className="pl-6 py-3 border-b border-[#D8DDE6] font-semibold">Client Identity</th>
                  <th className="py-3 border-b border-[#D8DDE6] font-semibold">Plan Tier</th>
                  <th className="py-3 border-b border-[#D8DDE6] font-semibold">System Status</th>
                  <th className="pr-6 py-3 border-b border-[#D8DDE6] text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8DDE6]">
                {companies.map(c => (
                  <tr key={c._id} className="group hover:bg-[#F7F9FB] transition-colors border-b border-[#D8DDE6] last:border-0 cursor-default">
                    <td className="pl-6 py-4">
                       <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3] font-bold">
                             {c.role === 'admin' ? <Crown size={16} className="text-[#0176D3]" /> : initials(c.companyName || c.name)}
                          </div>
                          <div className="space-y-0.5">
                            <div className="text-[14px] font-bold text-[#0176D3] hover:underline cursor-pointer truncate max-w-[220px]" onClick={() => setSelected(c)}>{c.companyName || c.name}</div>
                            <div className="text-[12px] text-[#706E6B]">{c.email}</div>
                          </div>
                       </div>
                    </td>
                    <td className="py-4">
                       <Badge variant="blue" className="text-[11px] font-semibold">
                          {c.subscriptionPlan?.name || 'TRIAL'}
                       </Badge>
                    </td>
                    <td className="py-4"><StatusBadge active={c.isActive !== false} /></td>
                    <td className="pr-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button className="p-2 rounded-md text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] transition-colors" title="View Profile" aria-label="View Profile" onClick={() => setSelected(c)}><Eye size={16} /></button>
                         <button className="p-2 rounded-md text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] transition-colors" title="Edit Profile" aria-label="Edit Profile" onClick={() => setEditing(c)}><Edit2 size={16} /></button>
                         <button className="p-2 rounded-md text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] transition-colors" title="Custom Overrides" aria-label="Custom Overrides" onClick={() => setOverriding(c)}><SlidersHorizontal size={16} /></button>
                         <button className="p-2 rounded-md text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] transition-colors disabled:opacity-30" title="Generate API Key" aria-label="Generate API Key" disabled={keyGenLoading && keyGenId === c._id} onClick={() => handleGenKey(c._id)}>
                            {keyGenLoading && keyGenId === c._id ? <Loader size={16} className="animate-spin" /> : <Key size={16} />}
                         </button>
                         <div className="w-px h-4 bg-[#D8DDE6] mx-1" />
                         <button className="p-2 rounded-md text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] transition-colors" title="Delete Account" aria-label="Delete Account" onClick={() => handleDelete(c._id, c.companyName || c.name)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-[#D8DDE6] flex flex-col md:flex-row justify-between items-center bg-[#F4F6F9] gap-4">
            <span className="text-[12px] text-[#706E6B]">
              Page <span className="font-bold text-[#080707]">{page}</span> of <span className="font-bold text-[#080707]">{totalPages}</span>
            </span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => load(page - 1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E5E5] bg-white text-[#706E6B] hover:bg-[#F3F2F2] disabled:opacity-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><ChevronLeft size={14} /></button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const n = i + Math.max(1, Math.min(page - 2, totalPages - 4));
                  if (n > totalPages || n < 1) return null;
                  return (
                    <button 
                       key={n} 
                       className={cn(
 "w-8 h-8 rounded-md text-[13px] font-semibold transition-colors flex items-center justify-center",
 page === n ? "bg-[#0176D3] text-white" : "bg-white border border-[#D8DDE6] text-[#706E6B] hover:bg-[#F3F2F2]"
 )} 
                       onClick={() => load(n)}
                    >
                       {n}
                    </button>
                  );
                })}
              </div>
 
              <button disabled={page >= totalPages} onClick={() => load(page + 1)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E5E5] bg-white text-[#706E6B] hover:bg-[#F3F2F2] disabled:opacity-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </Card>

      {selected && (
        <DetailModal
          company={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setSelected(null); }}
        />
      )}

      {editing && (
        <EditModal
          company={editing}
          plans={plans}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(page); }}
        />
      )}

      {overriding && (
        <OverridesModal
          company={overriding}
          catalogGroups={catalogGroups}
          onClose={() => setOverriding(null)}
          onSaved={() => { setOverriding(null); load(page); }}
        />
      )}
    </div>
  );
}

const initials = (name) => {
  return name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'CO';
};
